import React from 'react';
import { useInView } from '../hooks/useInView';

const integrations = [
  { label: 'QMS / eDMS', sub: 'Veeva Vault — live', color: '#00C9B1', live: true },
  { label: 'ERP', sub: 'Outbound queue (SAP / Oracle)', color: '#00C9B1', live: true },
  { label: 'MES', sub: 'Execution & orders — SDK', color: '#7B3FE4', live: false },
  { label: 'LIMS', sub: 'Lab results — SDK', color: '#7B3FE4', live: false },
  { label: 'Historians', sub: 'OSIsoft PI · SCADA — SDK', color: '#7B3FE4', live: false },
  { label: 'Identity', sub: 'LDAP · SSO', color: '#4C6EF5', live: false },
  { label: 'Data Lake / BI', sub: 'Analytics & export', color: '#F5A623', live: false },
  { label: 'Connector SDK', sub: 'Build any connector', color: '#E74C3C', live: false },
];

const platforms = [
  { icon: '▭', label: 'Desktop', desc: 'QA exception review, disposition & Part 11 e-signature ceremony.' },
  { icon: '▯', label: 'Tablet', desc: 'Operators capture in-process checks at the line — touch-first.' },
  { icon: '▢', label: 'Mobile', desc: 'Supervisors review, query & e-sign from any phone browser.' },
  { icon: '◻', label: 'Rugged PDA', desc: 'Handheld scanners capture on the shop floor.' },
  { icon: '⌗', label: 'LED / LCD Wall', desc: 'Panel PCs, HMIs & kiosks show live batch & release status.' },
];

export default function Integrations() {
  const [ref, inView] = useInView();

  return (
    <section id="integrations" style={{ padding: '120px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div ref={ref} style={{
          textAlign: 'center', marginBottom: 64,
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <p style={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#4C6EF5', marginBottom: 14, fontFamily: "'JetBrains Mono', monospace",
          }}>Integration-First</p>
          <h2 style={{
            fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 900, lineHeight: 1.08,
            letterSpacing: '-0.03em', color: '#fff', marginBottom: 16,
          }}>Plug into the entire plant.</h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.5)', maxWidth: 500, margin: '0 auto' }}>
            A connector framework, registry, and open SDK put Qualicore at the center of your stack.
          </p>
        </div>

        {/* Hub diagram */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 16,
          justifyContent: 'center', alignItems: 'center',
          marginBottom: 64,
        }}>
          {integrations.map((item, i) => {
            const [iRef, iInView] = useInView();
            return (
              <div key={item.label} ref={iRef} style={{
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${item.live ? item.color + '40' : 'rgba(255,255,255,0.07)'}`,
                borderRadius: 12,
                padding: '14px 18px',
                minWidth: 160,
                position: 'relative',
                opacity: iInView ? 1 : 0,
                transform: iInView ? 'scale(1)' : 'scale(0.9)',
                transition: `opacity 0.5s ease ${i * 60}ms, transform 0.5s ease ${i * 60}ms`,
              }}>
                {item.live && (
                  <div style={{
                    position: 'absolute', top: 10, right: 10,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00C9B1', animation: 'livePulse 2s infinite' }} />
                    <span style={{ fontSize: 9, color: '#00C9B1', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>LIVE</span>
                  </div>
                )}
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: "'JetBrains Mono', monospace" }}>{item.sub}</div>
              </div>
            );
          })}
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 14, padding: '16px 24px', marginBottom: 80,
        }}>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', fontFamily: "'JetBrains Mono', monospace" }}>
            Live today: Veeva Vault connector and the outbound ERP disposition queue. Roadmap: inbound ERP (Dynamics AX) and live MES / LIMS / historian connectors.
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)', marginBottom: 80 }} />

        {/* Runs Anywhere */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#00C9B1', marginBottom: 14, fontFamily: "'JetBrains Mono', monospace",
          }}>Runs Anywhere</p>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, letterSpacing: '-0.03em',
            color: '#fff', marginBottom: 14,
          }}>One platform. Every screen.</h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>
            Browser-based and server-authoritative — zero install, first-class on any device.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 14,
        }}>
          {platforms.map((p, i) => {
            const [pRef, pInView] = useInView();
            return (
              <div key={p.label} ref={pRef} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 14, padding: '24px 18px', textAlign: 'center',
                opacity: pInView ? 1 : 0,
                transform: pInView ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease ${i * 80}ms, transform 0.5s ease ${i * 80}ms`,
              }}>
                <div style={{
                  fontSize: 28, marginBottom: 12, color: '#00C9B1',
                  width: 52, height: 52, borderRadius: 12,
                  background: 'rgba(0,201,177,0.08)',
                  border: '1px solid rgba(0,201,177,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 14px',
                }}>{p.icon}</div>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 8 }}>{p.label}</h4>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>{p.desc}</p>
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: 28,
          background: 'rgba(0,201,177,0.05)',
          border: '1px solid rgba(0,201,177,0.12)',
          borderRadius: 12, padding: '14px 22px',
        }}>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
            <span style={{ color: '#00C9B1', fontWeight: 700 }}>Zero Install: </span>
            Responsive layouts adapt one governed UI to any screen — one app, not a build per device. Authority lives server-side; devices render the UI only.
          </p>
        </div>
      </div>
      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1;} 50%{opacity:0.3;} }
      `}</style>
    </section>
  );
}
